import type { ListResponse } from '@shared/schemas';

import api from '@/shared/api/api-client';

import { fileImportApiHttp } from '../../_shared/infrastructure/file-import-api.http';
import { licenceQueryHttp } from '../../_shared/infrastructure/licence-query.http';
import { listImportQueryHttp } from '../../_shared/infrastructure/list-import-query.http';
import { profileQueryHttp } from '../../_shared/infrastructure/profile-query.http';
import type { EditListGateway } from './edit-list.gateway';

/**
 * Real implementation over the shared api client. This is the single place that
 * knows the Edit List write endpoints and request shapes (kept 1:1 with the
 * original EditList component); the profile-selection reads come from the shared
 * `profileQueryHttp`.
 */
export const httpEditListGateway: EditListGateway = {
  loadList: (listId) => api.get<ListResponse>(`/lists/${listId}`),

  updateList: async (draft, { transferLicence }) => {
    await api.patch(`/lists/${draft.listId}`, {
      name: draft.name.trim(),
      start_date: draft.startDate,
      end_date: draft.endDate,
      scope: draft.scope.trim(),
      client_scope_description: draft.clientScopeDescription.trim(),
      status: draft.status,
      ...(transferLicence ? { licence_id: draft.licenceId } : {}),
    });
  },

  deleteList: async (listId) => {
    await api.delete(`/admin/lists/${listId}`);
  },

  importFile: async (listId, file) => {
    const payload: Record<string, unknown> = {
      list_id: listId,
      column_mapping: file.columnMapping,
      rows: file.rows,
    };
    if (file.isEnriched) payload.is_enriched = true;
    if (file.overwriteRows.length > 0) payload.overwrite_rows = file.overwriteRows;
    await api.post('/csv/import', payload);
  },

  associateProfiles: async (listId, profileIds) => {
    await api.post(`/lists/${listId}/profiles`, { profile_ids: profileIds });
  },

  queueEnrichment: async (listId, targetIds) => {
    await api.post('/enrichment-sessions', {
      type: 'linkedin_standard',
      list_id: listId,
      target_ids: targetIds,
    });
  },

  // ── Profile-selection data layer (shared with Add) ──
  ...profileQueryHttp,

  // ── File parse/analyze layer (shared with Add and the file-import feature) ──
  ...fileImportApiHttp,

  // ── Licence dropdown read (shared with Add) ──
  ...licenceQueryHttp,

  // ── Import-from-list step reads (shared with Add) ──
  ...listImportQueryHttp,
};
